import { FormErrorText } from '@exam-management/components';
import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigRandomType, QConfigStatus, QConfigTimeLimitType } from '@exam-management/graphql';
import {
  Box,
  Flex,
  FormRadio,
  FormTimeInput,
  Grid,
  RadioGroup,
  SectionTitle,
  Text,
} from '@shared/components';

import { useQConfigSettingsFormContext } from '../../providers';
import { ExamTimingByStructures } from '../exam-timing-by-structures';

interface ExamTimingSectionProps {
  disabled: boolean;
}

const OVERALL_TIMING_OPTION = {
  label: 'กำหนดระยะเวลาสอบรวมสำหรับทุกหมวด',
  value: QConfigTimeLimitType.All,
} as const;

const PARTS_TIMING_OPTION = {
  label: 'กำหนดระยะเวลาสอบแยกตามหมวด',
  value: QConfigTimeLimitType.ByStructure,
} as const;

export default function ExamTimingSection(props: Readonly<ExamTimingSectionProps>) {
  const { disabled: isDisabled } = props;
  const formContext = useQConfigSettingsFormContext();
  const { getValues, watch, resetField } = formContext;
  const { qConfigData } = useQConfig();
  const timingOption = watch('examTiming.option') ?? qConfigData.timeLimit?.type;
  const isDraftQConfig = qConfigData.status === QConfigStatus.Draft;

  const errorMessage = formContext.formState.errors.examTiming?.option?.root?.message;

  const handleResetStructuresTiming = () => {
    resetField('examTiming.structures', { defaultValue: [] });
  };

  const handleResetOverallTiming = () => {
    resetField('examTiming.overall', { defaultValue: null });
    if (getValues('randomization') === QConfigRandomType.AllRandom) {
      resetField('randomization', { defaultValue: null });
    }
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(8) }}>
      <SectionTitle title='Exam Timing' />
      <Grid
        sx={(theme) => ({
          gridTemplate: '1fr / 240px 1fr',
          columnGap: theme.spacing(6),
          rowGap: theme.spacing(4),
        })}
      >
        <Box>
          <Text variant='body1Semibold'>ระยะเวลาทำข้อสอบ</Text>
        </Box>
        <Box>
          <RadioGroup
            name='timing-option-group'
            sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
          >
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
              <FormRadio
                name='examTiming.option'
                label={OVERALL_TIMING_OPTION.label}
                value={OVERALL_TIMING_OPTION.value}
                disabled={!isDraftQConfig || isDisabled}
                // Remove this if we can set padding at Radio directly
                sx={{
                  '& .MuiButtonBase-root': {
                    py: (theme) => theme.spacing(0),
                  },
                }}
                onClick={handleResetStructuresTiming}
              />
              {timingOption === QConfigTimeLimitType.All ? (
                <Box sx={{ width: '100%', maxWidth: '256px', ml: (theme) => theme.spacing(8) }}>
                  <FormTimeInput
                    name='examTiming.overall'
                    label='กำหนดระยะเวลาสอบ (HH:MM)'
                    disabled={!isDraftQConfig || isDisabled}
                  />
                </Box>
              ) : null}
            </Flex>
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(3) }}>
              <FormRadio
                label={PARTS_TIMING_OPTION.label}
                name='examTiming.option'
                value={PARTS_TIMING_OPTION.value}
                // NOTE: Remove this if we can set padding at Radio directly
                sx={{
                  '& .MuiButtonBase-root': {
                    py: 0,
                  },
                }}
                disabled={!isDraftQConfig || isDisabled}
                onClick={handleResetOverallTiming}
              />
              {timingOption === QConfigTimeLimitType.ByStructure ? (
                <Flex sx={{ ml: (theme) => theme.spacing(8) }}>
                  <ExamTimingByStructures disabled={!isDraftQConfig || isDisabled} />
                </Flex>
              ) : null}
            </Flex>
            {errorMessage ? <FormErrorText>{errorMessage}</FormErrorText> : null}
          </RadioGroup>
        </Box>
      </Grid>
    </Flex>
  );
}
